import { z } from "zod";

function isValidCPF(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;
  const calc = (length: number) => {
    const sum = digits
      .slice(0, length)
      .split("")
      .reduce((acc, d, i) => acc + Number(d) * (length + 1 - i), 0);
    const rem = (sum * 10) % 11;
    return rem === 10 ? 0 : rem;
  };
  return calc(9) === Number(digits[9]) && calc(10) === Number(digits[10]);
}

function isValidCNPJ(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;
  const calc = (length: number) => {
    const weights =
      length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = digits
      .slice(0, length)
      .split("")
      .reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
    const rem = sum % 11;
    return rem < 2 ? 0 : 11 - rem;
  };
  return calc(12) === Number(digits[12]) && calc(13) === Number(digits[13]);
}

const cpfCnpjSchema = z
  .string()
  .min(11, "Informe um CPF ou CNPJ válido")
  .max(18, "Informe um CPF ou CNPJ válido")
  .refine(
    (val) => {
      const digits = val.replace(/\D/g, "");
      if (digits.length === 11) return isValidCPF(val);
      if (digits.length === 14) return isValidCNPJ(val);
      return false;
    },
    {
      message: "CPF ou CNPJ inválido",
    },
  );

const nomeCompletoSchema = z
  .string()
  .min(3, "Nome é obrigatório")
  .refine((val) => val.trim().split(" ").length > 1, {
    message: "Informe o nome completo",
  });

export const addressSchema = z.object({
  cep: z
    .string()
    .min(8, "CEP é obrigatório")
    .regex(/^[0-9]{5}-?[0-9]{3}$/, "CEP inválido"),
  logradouro: z.string().min(3, "Endereço é obrigatório"),
  numero: z
    .string()
    .min(1, "Número é obrigatório")
    .regex(
      /^(\d+|[Ss]\/?[Nn])$/,
      "Use apenas números (ex: 123) ou S/N para sem número",
    ),
  complemento: z.string().optional(),
  bairro: z.string().min(2, "Bairro é obrigatório"),
  cidade: z.string().optional(),
  estado: z.string().optional(),
  mun_codigo: z.string().min(1, "Município é obrigatório"),
  cep_pesquisado: z.string().optional(),
});

export const personalInfoSchema = z.object({
  name: nomeCompletoSchema,
  cpf: cpfCnpjSchema,
});

const cardSchema = z.object({
  name: z.string().min(3, "Nome é obrigatório"),
  number: z.string().min(13, "Número inválido"),
  expiry: z
    .string()
    .regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "Formato inválido (MM/AA)"),
  cvv: z.string().min(3, "CVV inválido").max(4, "CVV inválido"),
  brand: z.string().optional(),
});


export const clientInfoSchema = z.object({
  personalInfo: personalInfoSchema,
  address: addressSchema,
});

export type ClientInfoData = z.infer<typeof clientInfoSchema>;

export const paymentFormSchema = z.discriminatedUnion("paymentMethod", [
  z.object({
    paymentMethod: z.literal("credit"),
    installments: z.string(),
    card1: cardSchema,
    personalInfo: personalInfoSchema,
    address: addressSchema,
    termsAccepted: z.boolean().refine((val) => val === true, {
      message: "Você precisa aceitar os termos e condições",
    }),
  }),
  z.object({
    paymentMethod: z.literal("pix"),
    personalInfo: personalInfoSchema,
    address: addressSchema,
    termsAccepted: z.boolean().refine((val) => val === true, {
      message: "Você precisa aceitar os termos e condições",
    }),
  }),
  z.object({
    paymentMethod: z.literal("bank"),
    personalInfo: personalInfoSchema,
    address: addressSchema,
    termsAccepted: z.boolean().refine((val) => val === true, {
      message: "Você precisa aceitar os termos e condições",
    }),
  }),
]);

export type PaymentFormValues = z.infer<typeof paymentFormSchema>;
